import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  History,
  User,
  MapPin,
  BookOpen,
  Building2,
  LogIn,
  UserPlus,
  LogOut,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/common/Logo';
import { cn } from '@/lib/utils';
import { loginPath, signupPath } from '@/lib/returnTo';

interface MobileNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const publicLinks = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/centres', label: 'Find Recovry', icon: MapPin },
  { to: '/knowledge', label: 'Knowledge', icon: BookOpen },
  { to: '/partners', label: 'For Partners', icon: Building2 },
];

const authLinks = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/centres', label: 'Find Recovry', icon: MapPin },
  { to: '/history', label: 'History', icon: History },
  { to: '/profile', label: 'Profile', icon: User },
  { to: '/saved-addresses', label: 'Saved Addresses', icon: MapPin },
  { to: '/knowledge', label: 'Knowledge', icon: BookOpen },
  { to: '/partners', label: 'For Partners', icon: Building2 },
];

export function MobileNav({ open, onOpenChange }: MobileNavProps) {
  const location = useLocation();
  const { pathname } = location;
  const { isAuthenticated, signOut } = useAuth();

  const close = () => onOpenChange(false);

  const handleLogout = () => {
    signOut();
    close();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle>
            <Link to="/" onClick={close} aria-label="Recovry home" className="flex items-center">
              <Logo className="h-7" />
            </Link>
          </SheetTitle>
          <p className="text-xs text-muted-foreground">Reset. Recover. Perform.</p>
        </SheetHeader>

        <Separator />

        <nav className="flex flex-col gap-1 px-3 py-4" aria-label="Mobile navigation">
          {(isAuthenticated ? authLinks : publicLinks).map(({ to, label, icon: Icon }) => {
            const isActive = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={close}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/70 hover:bg-accent hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto border-t px-4 py-4">
          {isAuthenticated ? (
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <Button asChild variant="default" className="w-full" onClick={close}>
                <Link to={loginPath(location)}>
                  <LogIn className="mr-2 h-4 w-4" />
                  Login
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full" onClick={close}>
                <Link to={signupPath(location)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Sign Up
                </Link>
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
